import React from 'react';
import { Form, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import CascadingAddressSelector from 'components/CascadingAddressSelector';
import {
    ANALYZE_ADDRESS_SIMPLE,
    AnalyzeAddressSimpleResponse,
    AnalyzeAddressSimpleVariables,
} from '../graphql/analyzeAddressSimple';

export interface AddressResult {
    streetAddress?: string | null;
    unit?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    county?: string | null;
    latitude?: number | null;
    longitude?: number | null;
}

interface AddressInputModalProps {
    open: boolean;
    onConfirm: (result: AddressResult) => void;
    onCancel: () => void;
}

const AddressInputModal: React.FC<AddressInputModalProps> = ({ open, onConfirm, onCancel }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [analyzeAddress, { loading }] = useLazyQuery<AnalyzeAddressSimpleResponse, AnalyzeAddressSimpleVariables>(
        ANALYZE_ADDRESS_SIMPLE,
        { fetchPolicy: 'network-only' }
    );

    const handleOk = async () => {
        await form.validateFields();
        const values = form.getFieldsValue();

        const { data } = await analyzeAddress({
            variables: {
                streetAddress: values.addressLine1 || '',
                unit: values.unit || null,
                city: values.city || '',
                state: values.state || '',
                zipCode: values.zipcode || '',
            },
        });

        const result = data?.analyzeAddressSimple;
        const normalized = result?.normalizedComponents;

        onConfirm({
            streetAddress: normalized?.addressLine1 || values.addressLine1 || null,
            unit: values.unit || null,
            city: normalized?.cityName || values.city || null,
            state: normalized?.stateAbbreviation || values.state || null,
            zipCode: normalized?.zipcode || values.zipcode || null,
            county: 'US',
            latitude: result?.coordinates?.latitude ?? null,
            longitude: result?.coordinates?.longitude ?? null,
        });
        form.resetFields();
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={t('splitOrder.addressModal.title')}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            okText={t('splitOrder.addressModal.ok')}
            cancelText={t('splitOrder.addressModal.cancel')}
            confirmLoading={loading}
            width={680}
            destroyOnClose
        >
            <Form form={form} layout="vertical" className="mt-4">
                <Form.Item label={t('splitOrder.addressModal.addressLabel')}>
                    <CascadingAddressSelector
                        form={form}
                        countryWidth="20%"
                        stateWidth="20%"
                        cityWidth="30%"
                        zipcodeWidth="30%"
                        statePlaceholder={t('splitOrder.addressModal.statePlaceholder') ?? ''}
                        cityPlaceholder={t('splitOrder.addressModal.cityPlaceholder') ?? ''}
                        zipcodePlaceholder={t('splitOrder.addressModal.zipcodePlaceholder') ?? ''}
                    />
                </Form.Item>
                <div className="grid grid-cols-2 gap-x-4">
                    <Form.Item label={t('splitOrder.addressModal.detailLabel')} name="addressLine1">
                        <Input placeholder={t('splitOrder.addressModal.detailPlaceholder') ?? ''} />
                    </Form.Item>
                    <Form.Item label={t('splitOrder.addressModal.unitLabel')} name="unit">
                        <Input placeholder={t('splitOrder.addressModal.unitPlaceholder') ?? ''} />
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export default AddressInputModal;
